// The renderer, scene, lights and camera — every number from docs/ART_BIBLE.md §2 and §4.2 (the anchor frame
// art/lookdev/anchor_game.png is what this must reproduce). Quality tiers: high (desktop GPU), mid, low (Chromebook /
// software GL / weak phones): low drops shadow maps (contact blobs carry the grounding), MSAA, bloom and DPR > 1.

import * as THREE from "three";

export type Tier = "high" | "mid" | "low";

export const RAMP = new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 89, 153, 153, 209, 209, 255, 255, 255]);
export const gradientMap = (() => {
  const t = new THREE.DataTexture(RAMP, 16, 1, THREE.RedFormat);
  t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.colorSpace = THREE.NoColorSpace; t.needsUpdate = true;
  return t;
})();
export const SUN_DIR = new THREE.Vector3(-0.3687, 0.766, 0.5265).normalize();
export const SKY = "#C8D2F4", GROUND_BOUNCE = "#F0D2AC", SUN_COL = "#FFF2DE";

/** pick a starting tier from what the device reports (never a UA/model list): GPU string, cores, memory, touch */
export function detectTier(): Tier {
  const q = new URLSearchParams(location.search).get("q");
  if (q === "high" || q === "mid" || q === "low") return q;
  try { const s = localStorage.getItem("tnt.q"); if (s === "high" || s === "mid" || s === "low") return s; } catch {}
  let gpu = "";
  try {
    const gl = document.createElement("canvas").getContext("webgl2");
    const ext = gl?.getExtension("WEBGL_debug_renderer_info");
    gpu = ext ? String(gl!.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : "";
  } catch {}
  if (/swiftshader|llvmpipe|software|microsoft basic/i.test(gpu)) return "low";
  const cores = navigator.hardwareConcurrency || 4;
  const mem = (navigator as any).deviceMemory || 4;
  const touch = matchMedia("(pointer: coarse)").matches;
  if (mem <= 4 && cores <= 4) return "low";
  if (touch || mem <= 4) return "mid";
  return "high";
}

export class Stage {
  renderer: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(40, 16 / 9, 0.5, 200);
  hemi = new THREE.HemisphereLight(SKY, GROUND_BOUNCE, 0.56 * Math.PI);
  sun = new THREE.DirectionalLight(SUN_COL, 0.64 * Math.PI);
  tier: Tier;
  /** the camera rig: follows a point on the ground at a fixed pitch; distance is set per device by apparent size */
  focus = new THREE.Vector3(0, 0, 0);
  pitch = (55 * Math.PI) / 180;
  dist = 12;
  zoom = 1;                       // player zoom (scroll / pinch), clamped
  private shake = 0;

  constructor(public canvas: HTMLCanvasElement, tier: Tier) {
    this.tier = tier;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: tier !== "low", powerPreference: "high-performance", alpha: false, stencil: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.scene.background = new THREE.Color(SKY);
    this.scene.add(this.hemi, this.sun, this.sun.target);
    this.applyTier(tier);
    this.resize();
    addEventListener("resize", () => this.resize());
  }

  applyTier(tier: Tier) {
    this.tier = tier;
    const r = this.renderer;
    r.setPixelRatio(Math.min(devicePixelRatio || 1, tier === "low" ? 1 : tier === "mid" ? 1.25 : 1.5));
    r.shadowMap.enabled = tier !== "low";
    r.shadowMap.type = THREE.PCFSoftShadowMap;
    this.sun.castShadow = tier !== "low";
    const ms = tier === "high" ? 2048 : 1024;
    this.sun.shadow.mapSize.set(ms, ms);
    this.sun.shadow.map?.dispose(); (this.sun.shadow as any).map = null;
    Object.assign(this.sun.shadow.camera, { left: -14, right: 14, top: 14, bottom: -14, near: 1, far: 60 });
    this.sun.shadow.camera.updateProjectionMatrix();
    this.sun.shadow.bias = -0.0004; this.sun.shadow.normalBias = 0.02;
    try { localStorage.setItem("tnt.q.auto", tier); } catch {}
  }

  /** size the camera by apparent angle: a townie should read at a similar fraction of the screen's short side on every
   *  device (a phone is held at half a monitor's distance — memory feedback_two_builds_desktop_mobile) */
  resize() {
    const w = this.canvas.clientWidth || innerWidth, h = this.canvas.clientHeight || innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // portrait: widen the vertical FOV so the square's width still fits
    this.camera.fov = w >= h ? 40 : Math.min(62, 40 * (h / w) * 0.72);
    this.camera.updateProjectionMatrix();
  }

  setFollow(x: number, z: number, inspector: boolean) {
    this.focus.set(x, 0.6, z);
    const touch = matchMedia("(pointer: coarse)").matches;
    // wider than the anchor's 12 m: this game is about reading a crowd (and the Inspector needs the most of it)
    const base = (inspector ? 18 : 14.5) * (touch ? 0.9 : 1);
    this.dist = base * this.zoom;
  }
  kick(amount: number) { this.shake = Math.min(0.35, this.shake + amount); }

  /** place the camera for this frame (smoothly) and the sun's shadow box around the focus */
  frame(dt: number) {
    const cx = this.focus.x, cz = this.focus.z;
    const want = new THREE.Vector3(cx, this.focus.y + Math.sin(this.pitch) * this.dist, cz + Math.cos(this.pitch) * this.dist);
    const k = 1 - Math.exp(-dt * 6);
    this.camera.position.lerp(want, k);
    if (this.shake > 0) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.y += (Math.random() - 0.5) * this.shake * 0.5;
      this.shake = Math.max(0, this.shake - dt * 1.4);
    }
    // look at the focus but keep the pitch exact (no lerp drift): derive the look target from the camera itself
    const p = this.camera.position;
    this.camera.lookAt(p.x, p.y - Math.sin(this.pitch) * this.dist, p.z - Math.cos(this.pitch) * this.dist);
    this.sun.position.copy(this.focus).addScaledVector(SUN_DIR, 30);
    this.sun.target.position.copy(this.focus);
  }
  snapCamera() { this.shake = 0; this.frame(10); this.camera.position.set(this.focus.x, this.focus.y + Math.sin(this.pitch) * this.dist, this.focus.z + Math.cos(this.pitch) * this.dist); this.frame(0); }

  render() { this.renderer.render(this.scene, this.camera); }
}

/** glow on a vertex-coloured toon material: emissive = albedo (the vertex colour) × glow — capsules, lamp glass, bus
 *  lights (manifest material.extras.glow). three's emissive is a single colour, so the shader adds vColor × glow. */
export function glowify(m: THREE.MeshToonMaterial, glow: number) {
  if (glow <= 0) return m;
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uGlow = { value: glow };
    sh.fragmentShader = `uniform float uGlow;\n` + sh.fragmentShader.replace("#include <emissivemap_fragment>",
      `#include <emissivemap_fragment>
#ifdef USE_COLOR
 totalEmissiveRadiance += vColor.rgb * uGlow;
#else
 totalEmissiveRadiance += diffuseColor.rgb * uGlow;
#endif`);
  };
  m.customProgramCacheKey = () => "glow" + glow;
  return m;
}

/** the house material: MeshToonMaterial on the 16-texel ramp; glow items get emissive = albedo × glow */
export function toon(map: THREE.Texture | null, color?: THREE.ColorRepresentation, glow = 0): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({ gradientMap, color: color ?? 0xffffff });
  if (map) { map.colorSpace = THREE.SRGBColorSpace; m.map = map; }
  if (glow > 0) { m.emissive = new THREE.Color(color ?? 0xffffff); if (map) m.emissiveMap = map; m.emissiveIntensity = glow; }
  return m;
}
